import { useUser } from "@/auth/provider/UserContext";
import { useCurrentProgram } from "@/components/programs/CurrentProgramProvider";
import { useModules } from "@/entitlements/useModules";
import { EXPLORE_DASHBOARD, EXPLORE_REPORTS, HOME } from "@/routing";
import AssuredWorkloadOutlined from "@mui/icons-material/AssuredWorkloadOutlined";
import BoltOutlined from "@mui/icons-material/BoltOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import DataExplorationOutlined from "@mui/icons-material/DataExplorationOutlined";
import HomeOutlined from "@mui/icons-material/HomeOutlined";
import SummarizeOutlined from "@mui/icons-material/SummarizeOutlined";
import { PropsWithChildren } from "react";
import { RouteContext } from "./routeContext";
import {
  AppRoute,
  buildBuildingPerformanceProgramsRoutes,
  EnergyManagementRoutes,
  getSecondaryRoutes,
  PerformanceComplianceRoutes,
} from "./routes";

export const RouteProvider = ({ children }: PropsWithChildren) => {
  const { user } = useUser();
  const { currentProgram } = useCurrentProgram();
  const {
    hasEnergyManagement,
    hasPerformanceCompliance,
    hasBuildingPerformancePrograms,
  } = useModules();

  const moduleRoutes: AppRoute[] = [];

  // Modules are always ordered: Energy Management, Performance Compliance,
  // Building Performance Programs. The first accessible module is expanded.
  const firstExpanded = hasEnergyManagement;
  const secondExpanded = !hasEnergyManagement && hasPerformanceCompliance;
  const thirdExpanded = !hasEnergyManagement && !hasPerformanceCompliance;

  if (hasEnergyManagement) {
    moduleRoutes.push({
      icon: <BoltOutlined />,
      text: "Energy Management",
      defaultExpanded: firstExpanded,
      children: EnergyManagementRoutes,
    });
  }

  if (hasPerformanceCompliance) {
    moduleRoutes.push({
      icon: <CheckCircleOutlined />,
      text: "Performance Compliance",
      defaultExpanded: secondExpanded,
      children: PerformanceComplianceRoutes,
    });
  }

  if (hasBuildingPerformancePrograms) {
    moduleRoutes.push({
      icon: <AssuredWorkloadOutlined />,
      text: "Building Performance Programs",
      defaultExpanded: thirdExpanded,
      children: buildBuildingPerformanceProgramsRoutes(currentProgram),
    });
  }

  const primaryRoutes: AppRoute[] = [
    {
      icon: <HomeOutlined />,
      text: "Home",
      href: HOME,
    },
    {
      icon: <DataExplorationOutlined />,
      text: "Explore",
      defaultExpanded: true,
      children: [
        {
          text: "Dashboards",
          icon: <DashboardOutlined />,
          href: EXPLORE_DASHBOARD,
        },
        {
          text: "Reports",
          icon: <SummarizeOutlined />,
          href: EXPLORE_REPORTS,
        },
      ],
    },
    ...moduleRoutes,
  ];

  const secondaryRoutes = getSecondaryRoutes(user.is_super);

  return (
    <RouteContext.Provider value={{ primaryRoutes, secondaryRoutes }}>
      {children}
    </RouteContext.Provider>
  );
};
